/**
 * 网络监控模块
 * 采集页面级别的 fetch/XMLHttpRequest 请求
 */

import type {
  RequestData,
  RequestFilter,
  PageRequestsStore,
  PendingRequestData,
  NetworkModuleConfig,
} from '../types'
import type { ResponseInfo } from '../types/network'
import {
  interceptFetch,
  interceptXHR,
  generateRequestId,
  type PendingRequest,
} from '../utils/request-interceptor'
import { createRequestData, filterRequests } from '../collectors/network'
import type { RouteChangeManager, RouteChangeInfo, Unsubscribe } from '../core/route-change-manager'

/**
 * 网络监控模块
 */
export class NetworkModule {
  private config: NetworkModuleConfig

  /** 请求存储（按页面 URL 组织） */
  private requestStore: Map<string, PageRequestsStore> = new Map()

  /** 待处理的请求（请求开始但未结束） */
  private pendingRequests: Map<string, PendingRequestData> = new Map()

  /** 当前页面 URL */
  private currentPageUrl: string = ''

  /** 是否已初始化 */
  private isInitialized = false

  /** 拦截器销毁函数 */
  private destroyFetchInterceptor: (() => void) | null = null
  private destroyXHRInterceptor: (() => void) | null = null

  /** 路由变更管理器 */
  private routeChangeManager: RouteChangeManager
  /** 路由订阅取消函数 */
  private unsubscribeRouteChange: Unsubscribe | null = null

  constructor(config: NetworkModuleConfig, routeChangeManager: RouteChangeManager) {
    this.config = config
    this.routeChangeManager = routeChangeManager
  }

  /**
   * 初始化模块
   */
  init(): void {
    if (this.isInitialized) return
    if (typeof window === 'undefined') return

    this.isInitialized = true
    this.currentPageUrl = location.href

    // 初始化拦截器
    this.setupInterceptors()

    // 订阅路由变化
    this.unsubscribeRouteChange = this.routeChangeManager.subscribe(this.handleRouteChange)

    // this.log('Network module initialized')
  }

  /**
   * 设置拦截器
   */
  private setupInterceptors(): void {
    const interceptorConfig = {
      recordHeaders: this.config.recordHeaders,
      recordBody: this.config.recordBody,
      recordResponse: this.config.recordResponse,
      maxBodySize: this.config.maxBodySize,
      maxResponseSize: this.config.maxResponseSize,
      excludeUrls: this.config.excludeUrls,
    }
    // 设置拦截器回调
    const callbacks = {
      onRequestStart: (request: PendingRequest): string => {
        return this.handleRequestStart(request)
      },
      onRequestEnd: (requestId: string, response: ResponseInfo): void => {
        this.handleRequestEnd(requestId, response)
      },
    }

    // 拦截 fetch
    this.destroyFetchInterceptor = interceptFetch(callbacks, interceptorConfig)

    // 拦截 XHR
    this.destroyXHRInterceptor = interceptXHR(callbacks, interceptorConfig)
  }

  
  /**
   * 处理路由变化
   */
  private handleRouteChange = (info: RouteChangeInfo): void => {
    this.log('Route changed', { from: info.oldUrl, to: info.newUrl })

    // 更新当前页面 URL
    this.currentPageUrl = info.newUrl

    // 清理过期数据
    this.pruneOldData()
  }


  /**
   * 处理请求开始
   */
  private handleRequestStart(request: PendingRequest): string {
    const requestId = generateRequestId()
    const currentUrl = location.href

    // 创建待处理请求记录
    const pendingData: PendingRequestData = {
      id: requestId,
      url: request.url,
      method: request.method,
      type: request.type,
      requestHeaders: request.headers,
      requestBody: request.body,
      startTime: Date.now(),
      pageUrl: currentUrl, // 记录发起请求时的页面 URL
    }

    this.pendingRequests.set(requestId, pendingData)

    // this.log('Request started', { id: requestId, url: request.url, method: request.method })

    return requestId
  }

  /**
   * 处理请求结束
   */
  private handleRequestEnd(requestId: string, response: ResponseInfo): void {
    const pending = this.pendingRequests.get(requestId)
    if (!pending) {
      this.log('请求未找到', { id: requestId })
      return
    }

    // 从待处理列表移除
    this.pendingRequests.delete(requestId)

    // 创建完整请求数据
    const requestData = createRequestData(pending, {
      ...response,
      endTime: response.endTime || Date.now(),
    })
    // 存储请求
    this.storeRequest(pending.pageUrl, requestData)

    // this.log('Request ended', {
    //   id: requestId,
    //   url: requestData.url,
    //   status: requestData.status,
    //   duration: requestData.duration,
    // })
  }

  /**
   * 存储请求
   */
  private storeRequest(pageUrl: string, request: RequestData): void {
    let pageStore = this.requestStore.get(pageUrl)

    if (!pageStore) {
      pageStore = {
        pageUrl,
        requests: [],
        lastUpdated: Date.now(),
      }
      this.requestStore.set(pageUrl, pageStore)
    }

    pageStore.requests.push(request)
    pageStore.lastUpdated = Date.now()

    // 检查单页请求数限制
    if (pageStore.requests.length > (this.config.maxRequestsPerPage || 50)) {
      pageStore.requests.shift() // 移除最早的请求
      this.log('Page requests limit reached, removed oldest request')
    }

    // 清理过期数据
    this.pruneOldData()
  }

  /**
   * 清理过期数据
   */
  private pruneOldData(): void {
    const maxPages = this.config.maxPages || 5

    // 如果页面数超过限制，移除最久未更新的页面
    if (this.requestStore.size > maxPages) {
      // 按最后更新时间排序
      const sortedPages = Array.from(this.requestStore.entries())
        .sort((a, b) => a[1].lastUpdated - b[1].lastUpdated)

      // 移除最旧的页面
      const toRemove = sortedPages.slice(0, this.requestStore.size - maxPages)
      for (const [pageUrl] of toRemove) {
        this.requestStore.delete(pageUrl)
        this.log('Removed old page data', { pageUrl })
      }
    }
  }

  /**
   * 获取指定页面的请求列表
   * @param pageUrl - 页面 URL，可选。不提供则返回所有页面的请求
   * @param filter - 过滤条件
   * @returns 符合条件的请求列表
   */
  getRequestsForPage(pageUrl?: string | RequestFilter, filter?: RequestFilter): RequestData[] {
    // 支持只传 filter 参数，获取所有页面的请求
    if (arguments.length === 0) {
      // 不传参数，返回所有页面的请求
      return this.getAllPageRequests(filter)
    }

    // 如果第一个参数是过滤条件，则作为全局过滤处理
    if (pageUrl && typeof pageUrl === 'object' && !filter) {
      filter = pageUrl
      return this.getAllPageRequests(filter)
    }

    // 按指定页面 URL 获取
    const actualPageUrl = pageUrl as string
    const pageStore = this.requestStore.get(actualPageUrl)
    if (!pageStore) return []

    const requests = [...pageStore.requests]
    return filter ? filterRequests(requests, filter) : requests
  }

  /**
   * 获取所有页面的请求列表
   * @param filter - 过滤条件
   * @returns 符合条件的请求列表
   */
  getAllPageRequests(filter?: RequestFilter): RequestData[] {
    const allRequests: RequestData[] = []
    for (const pageStore of this.requestStore.values()) {
      allRequests.push(...pageStore.requests)
    }
    return filter ? filterRequests(allRequests, filter) : allRequests
  }

  /**
   * 获取当前页面的请求列表
   * @param filter - 过滤条件
   * @returns 符合条件的请求列表
   */
  getCurrentPageRequests(filter?: RequestFilter): RequestData[] {
    return this.getRequestsForPage(this.currentPageUrl, filter)
  }

  /**
   * 根据事件类型获取请求列表
   * @param eventType - 事件类型（requestStatus）
   * @param pageUrl - 页面 URL，可选。不提供则在所有页面中查找
   * @returns 符合条件的请求列表
   */
  getRequestsByEventType(
    eventType: 'success' | 'error' | 'timeout' | 'abort' | 'pending',
    pageUrl?: string
  ): RequestData[] {
    const filter: RequestFilter = {
      requestStatus: eventType
    }

    if (pageUrl) {
      return this.getRequestsForPage(pageUrl, filter)
    } else {
      return this.getAllPageRequests(filter)
    }
  }

  /**
   * 清除指定页面的请求记录
   */
  clearPageRequests(pageUrl: string): void {
    this.requestStore.delete(pageUrl)
    this.log('Cleared page requests', { pageUrl })
  }

  /**
   * 清除所有请求记录
   */
  clearAllRequests(): void {
    this.requestStore.clear()
    this.pendingRequests.clear()
    this.log('Cleared all requests')
  }

  /**
   * 获取当前页面 URL
   */
  getCurrentPageUrl(): string {
    return this.currentPageUrl
  }

  /**
   * 获取存储统计信息
   */
  getStorageStats(): {
    totalPages: number
    totalRequests: number
    pendingRequests: number
  } {
    let totalRequests = 0
    for (const pageStore of this.requestStore.values()) {
      totalRequests += pageStore.requests.length
    }

    return {
      totalPages: this.requestStore.size,
      totalRequests,
      pendingRequests: this.pendingRequests.size,
    }
  }

  /**
   * 销毁模块
   */
  destroy(): void {
    if (!this.isInitialized) return

    // 移除拦截器
    this.destroyFetchInterceptor?.()
    this.destroyXHRInterceptor?.()

    // 取消路由变化订阅
    this.unsubscribeRouteChange?.()
    this.unsubscribeRouteChange = null

    // 清理数据
    this.requestStore.clear()
    this.pendingRequests.clear()

    // 清理状态
    this.destroyFetchInterceptor = null
    this.destroyXHRInterceptor = null
    this.isInitialized = false

    this.log('Network 模块已销毁')
  }

  /**
   * 调试
   */
  private log(message: string, data?: unknown): void {
    if (this.config.debug) {
      console.log('%c[Tracker Network] %c ' + message, 'background: #E6A23C; color: #fff;', 'color: inherit;', data)
    }
  }
}
